/**
 * Media Modal — "Add media to post" variant
 *
 * Shown when a user already has a post open and wants to add more media.
 * Uses shadcn Dialog with the same dimensions as the "new post" media picker,
 * a "Select media" header, and a simplified action bar (Cancel + "Add to post").
 *
 * Design ref: Figma node 2081-18788 (Create Post file)
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PostMediaFile } from "shared/types/post";
import { CollectionFile } from "shared/types/files";
import { MediaPicker } from "../media-picker/media-picker";
import { ActionBar } from "../media-picker-v2/action-bar";

export function MediaModal(props: {
  onSelect(files: PostMediaFile[]): Promise<void>;
  limit: number;
  show: boolean;
  onHide(): void;
  selectedAccountIds: string[];
}) {
  const [selectedFiles, setSelectedFiles] = useState<CollectionFile[]>([]);
  const footerRef = useRef<{
    handleSubmit?: () => void;
    handleFileRemove?: (file: CollectionFile) => void;
    handleFilesClear?: () => void;
  }>({});

  const clearSelection = useCallback(() => {
    footerRef.current.handleFilesClear?.();
    setSelectedFiles([]);
  }, []);

  const handleHide = () => {
    props.onHide();
  };

  const handleSelect = async (files: PostMediaFile[]) => {
    await props.onSelect(files);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      handleHide();
    }
  };

  useEffect(() => {
    if (!props.show) {
      clearSelection();
    }
  }, [clearSelection, props.show]);

  const handleAddToPost = () => {
    footerRef.current.handleSubmit?.();
  };

  return (
    <Dialog open={props.show} onOpenChange={handleOpenChange}>
      <DialogContent
        className="w-[90vw] max-w-[1400px] max-h-[90vh] p-0 flex flex-col"
        contentWrapperClassName="px-0"
        showCloseButton={false}
      >
        {/* Header — "Select Media" per Figma design */}
        <DialogHeader className="px-6 py-4 flex-shrink-0">
          <DialogTitle className="font-gotham text-2xl font-bold text-[#333]">
            Select Media
          </DialogTitle>
        </DialogHeader>

        {/* Media picker body — reuses v1 MediaPicker for data management */}
        <DialogBody className="flex-1">
          <MediaPicker
            hideFooter={true}
            onSelectionChange={setSelectedFiles}
            footerRef={footerRef}
            persistState={false}
            hideCloseButton={true}
            limit={props.limit}
            onClose={handleHide}
            onSelect={handleSelect}
            selectedAccountIds={props.selectedAccountIds}
          />
        </DialogBody>

        {/* Action bar — add-to-post mode: Cancel + "Add to post" only */}
        <div className="flex-shrink-0">
          <ActionBar
            mode="add-to-post"
            onCancel={handleHide}
            onCreatePost={handleAddToPost}
            createPostDisabled={selectedFiles.length === 0}
            selectedCount={selectedFiles.length}
            maxCount={props.limit}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
